import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(
        /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId)
      );
    }

    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');

    if (!form?.checkValidity()) return;

    const productForm = /** @type {ProductFormComponent | null} */ (
      this.closest('product-form-component')
    );

    const quantitySelector = productForm?.refs.quantitySelector;

    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        return;
      }
    }

    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const isQuickAdd =
        this.refs.addToCartButton.classList.contains('quick-add__button');

      const animationEnabled =
        this.dataset.addToCartAnimation === 'true';

      /*
       * Prairie Street:
       *
       * Quick Add waits until Shopify confirms SUCCESS
       * before showing the add-to-cart animation.
       *
       * This prevents false motion when Gift Guard
       * blocks the product.
       */
      if (
        !isQuickAdd &&
        animationEnabled &&
        !event.target.closest('.quick-add-modal')
      ) {
        this.#animateFlyToCart();
      }

      if (!isQuickAdd) {
        this.animateAddToCart();
      }
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement('fly-to-cart')
    );

    const flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${image})`
    );

    flyToCartElement.style.setProperty(
      '--start-opacity',
      '0'
    );

    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId)
    );

    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      const index = this.#resetTimeouts.indexOf(timeoutId);

      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define(
    'add-to-cart-component',
    AddToCartComponent
  );
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];

  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');

    target?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      { signal }
    );

    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    );

    document.addEventListener(
      ThemeEvents.cartUpdate,
      this.#onCartUpdate,
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );

    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find(
      (item) =>
        item.variant_id.toString() ===
        variantIdInput.value.toString()
    );

    const cartQty =
      cartItem ? cartItem.quantity : 0;

    const quantitySelector = /** @type {any | undefined} */ (
      this.querySelector('quantity-selector-component')
    );

    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );

    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error(
        'Failed to fetch cart quantity:',
        error
      );

      return 0;
    }
  }

  /**
   * Prairie Street Gift Order error.
   *
   * PDP uses the normal inline theme error.
   * Quick Add uses an alert because the product card
   * has no reliable space for the full message.
   *
   * @param {string} message
   * @param {HTMLElement | undefined} addToCartTextError
   */
  #showGiftOrderError(
    message,
    addToCartTextError
  ) {
    const isQuickAdd =
      this.classList.contains(
        'quick-add__product-form-component'
      );

    if (isQuickAdd) {
      window.alert(message);
      this.#setLiveRegionText(message);

      if (this.#timeout) {
        clearTimeout(this.#timeout);
      }

      this.#timeout = setTimeout(() => {
        this.#clearLiveRegionText();
      }, ERROR_MESSAGE_DISPLAY_DURATION);

      return;
    }

    if (addToCartTextError) {
      addToCartTextError.classList.remove(
        'hidden'
      );

      let messageNode =
        addToCartTextError.querySelector(
          '[data-gift-order-error-text]'
        );

      if (!messageNode) {
        messageNode =
          document.createElement('span');

        messageNode.setAttribute(
          'data-gift-order-error-text',
          ''
        );

        addToCartTextError.appendChild(
          messageNode
        );
      }

      messageNode.textContent = message;
    }

    this.#setLiveRegionText(message);

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    this.#timeout = setTimeout(() => {
      if (addToCartTextError) {
        addToCartTextError.classList.add(
          'hidden'
        );

        const messageNode =
          addToCartTextError.querySelector(
            '[data-gift-order-error-text]'
          );

        messageNode?.remove();
      }

      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    if (
      event.detail?.sourceId === this.id ||
      event.detail?.data?.source === 'product-form-component'
    ) {
      return;
    }

    const cart = /** @type {Cart} */ (
      event.detail?.resource
    );

    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;

    event.preventDefault();

    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }

    const allAddToCartContainers =
      /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll(
          'add-to-cart-component'
        )
      );

    const anyButtonDisabled =
      Array.from(
        allAddToCartContainers
      ).some(
        (container) =>
          container.refs
            .addToCartButton?.disabled
      );

    if (anyButtonDisabled) return;

    const form = this.querySelector('form');

    if (!form) {
      throw new Error(
        'Product form element missing'
      );
    }

    /*
     * ==========================================
     * PRAIRIE STREET GIFT ORDER GUARD
     * ==========================================
     *
     * Normal Order + Regular Product = ALLOW
     * Normal Order + Gift Product = BLOCK
     * Gift Order + Gift Product = ALLOW
     * Gift Order + Regular Product = BLOCK
     *
     * Gift context is identified by:
     *
     * ?gift=true
     *
     * OR
     *
     * /collections/gift-products
     */

    try {
      const cartResponse =
        await fetch('/cart.js', {
          headers: {
            Accept: 'application/json',
          },
        });

      if (!cartResponse.ok) {
        throw new Error(
          'Unable to verify cart'
        );
      }

      const currentCart =
        await cartResponse.json();

      const giftOrderActive =
        currentCart?.attributes
          ?.gift_order === 'true';

      const params =
        new URLSearchParams(
          window.location.search
        );

      const isGiftProduct =
        params.get('gift') === 'true' ||
        window.location.pathname.includes(
          '/collections/gift-products'
        );

      const cartHasItems =
        Number(
          currentCart?.item_count || 0
        ) > 0;

      /*
       * CASE 1
       *
       * Gift Order active.
       * Customer tries to add regular product.
       */
      if (
        giftOrderActive &&
        !isGiftProduct
      ) {
        this.#showGiftOrderError(
          'Your current cart is a gift order. Regular Prairie Street products must be purchased separately. Please complete your gift order before adding regular products.',
          addToCartTextError
        );

        return;
      }

      /*
       * CASE 2
       *
       * Gift Product without active Gift Order.
       */
      if (
        !giftOrderActive &&
        isGiftProduct
      ) {
        const errorMessage =
          cartHasItems
            ? 'Your cart contains regular Prairie Street products. Gift products must be purchased separately. Please complete or remove the current items before starting a gift order.'
            : 'Please start your gift order first. Enter the recipient details before adding gift products.';

        this.#showGiftOrderError(
          errorMessage,
          addToCartTextError
        );

        return;
      }

    } catch (error) {
      console.error(
        'Gift order validation failed:',
        error
      );

      this.#showGiftOrderError(
        'We could not verify your cart. Please try adding the product again.',
        addToCartTextError
      );

      return;
    }

    /*
     * Original quantity validation
     */

    if (
      this.refs.quantitySelector
        ?.canAddToCart
    ) {
      const validation =
        this.refs.quantitySelector
          .canAddToCart();

      if (!validation.canAdd) {
        for (
          const container
          of allAddToCartContainers
        ) {
          container.disable();
        }

        const errorTemplate =
          this.dataset.quantityErrorMax || '';

        const errorMessage =
          errorTemplate.replace(
            '{{ maximum }}',
            validation.maxQuantity
              ?.toString() || ''
          );

        if (addToCartTextError) {
          addToCartTextError.classList.remove(
            'hidden'
          );

          const textNode =
            addToCartTextError.childNodes[2];

          if (textNode) {
            textNode.textContent =
              errorMessage;
          } else {
            const newTextNode =
              document.createTextNode(
                errorMessage
              );

            addToCartTextError.appendChild(
              newTextNode
            );
          }

          this.#setLiveRegionText(
            errorMessage
          );

          if (this.#timeout) {
            clearTimeout(this.#timeout);
          }

          this.#timeout =
            setTimeout(() => {
              if (!addToCartTextError) return;

              addToCartTextError.classList.add(
                'hidden'
              );

              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (
            const container
            of allAddToCartContainers
          ) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData =
      new FormData(form);

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    let cartItemComponentsSectionIds = [];

    cartItemsComponents.forEach(
      (item) => {
        if (
          item instanceof HTMLElement &&
          item.dataset.sectionId
        ) {
          cartItemComponentsSectionIds.push(
            item.dataset.sectionId
          );
        }

        formData.append(
          'sections',
          cartItemComponentsSectionIds.join(
            ','
          )
        );
      }
    );

    const fetchCfg =
      fetchConfig(
        'javascript',
        { body: formData }
      );

    fetch(
      Theme.routes.cart_add_url,
      {
        ...fetchCfg,

        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      }
    )

      .then(
        (response) =>
          response.json()
      )

      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute('id') || '',
              response.message,
              response.description,
              response.errors
            )
          );

          if (!addToCartTextError) {
            return;
          }

          addToCartTextError.classList.remove(
            'hidden'
          );

          const textNode =
            addToCartTextError.childNodes[2];

          if (textNode) {
            textNode.textContent =
              response.message;
          } else {
            const newTextNode =
              document.createTextNode(
                response.message
              );

            addToCartTextError.appendChild(
              newTextNode
            );
          }

          this.#setLiveRegionText(
            response.message
          );

          this.#timeout =
            setTimeout(() => {
              if (!addToCartTextError) {
                return;
              }

              addToCartTextError.classList.add(
                'hidden'
              );

              this.#clearLiveRegionText();

            }, ERROR_MESSAGE_DISPLAY_DURATION);

          this.dispatchEvent(
            new CartAddEvent(
              {},
              this.id,
              {
                didError: true,
                source:
                  'product-form-component',

                itemCount:
                  Number(
                    formData.get(
                      'quantity'
                    )
                  ) ||
                  Number(
                    this.dataset
                      .quantityDefault
                  ),

                productId:
                  this.dataset.productId,
              }
            )
          );

          return;
        }

        const id =
          formData.get('id');

        if (addToCartTextError) {
          addToCartTextError.classList.add(
            'hidden'
          );

          addToCartTextError.removeAttribute(
            'aria-live'
          );
        }

        if (!id) {
          throw new Error(
            'Form ID is required'
          );
        }

        /*
         * Prairie Street
         *
         * Quick Add animation happens only AFTER
         * Shopify confirms that the product was
         * successfully added.
         */
        for (
          const container
          of allAddToCartContainers
        ) {
          const button =
            container.refs.addToCartButton;

          if (
            button?.classList.contains(
              'quick-add__button'
            )
          ) {
            container.animateAddToCart();
          }
        }

        const anyAddToCartButton =
          allAddToCartContainers[0]
            ?.refs.addToCartButton;

        if (anyAddToCartButton) {
          const addedTextElement =
            anyAddToCartButton.querySelector(
              '.add-to-cart-text--added'
            );

          const addedText =
            addedTextElement
              ?.textContent
              ?.trim() ||
            Theme.translations.added;

          this.#setLiveRegionText(
            addedText
          );

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        await this.#fetchAndUpdateCartQuantity();

        this.dispatchEvent(
          new CartAddEvent(
            {},
            id.toString(),
            {
              source:
                'product-form-component',

              itemCount:
                Number(
                  formData.get(
                    'quantity'
                  )
                ) ||
                Number(
                  this.dataset
                    .quantityDefault
                ),

              productId:
                this.dataset.productId,

              sections:
                response.sections,
            }
          )
        );
      })

      .catch((error) => {
        console.error(error);
      })

      .finally(() => {
        cartPerformance.measureFromEvent(
          'add:user-action',
          event
        );
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel =
      this.refs.quantityLabelCartCount;

    if (quantityLabel) {
      const inCartText =
        quantityLabel.textContent?.match(
          /\((\d+)\s+(.+)\)/
        );

      if (
        inCartText &&
        inCartText[2]
      ) {
        quantityLabel.textContent =
          `(${cartQty} ${inCartText[2]})`;
      }

      quantityLabel.classList.toggle(
        'hidden',
        cartQty === 0
      );
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion =
      this.refs.liveRegion;

    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion =
      this.refs.liveRegion;

    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement
   * @param {Element | null | undefined} newElement
   * @param {Element | null} [insertReferenceElement]
   */
  #morphOrUpdateElement(
    currentElement,
    newElement,
    insertReferenceElement = null
  ) {
    if (
      currentElement &&
      newElement
    ) {
      morph(
        currentElement,
        newElement
      );

    } else if (
      currentElement &&
      !newElement
    ) {
      currentElement.remove();

    } else if (
      !currentElement &&
      newElement &&
      insertReferenceElement
    ) {
      insertReferenceElement
        .insertAdjacentElement(
          'beforebegin',
          /** @type {Element} */ (
            newElement.cloneNode(true)
          )
        );
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (
      event.detail.data.newProduct
    ) {
      this.dataset.productId =
        event.detail.data.newProduct.id;

    } else if (
      event.detail.data.productId !==
      this.dataset.productId
    ) {
      return;
    }

    const { variantId } =
      this.refs;

    variantId.value =
      event.detail.resource?.id ?? '';

    const {
      addToCartButtonContainer:
        currentAddToCartButtonContainer,

      acceleratedCheckoutButtonContainer,
    } = this.refs;

    const currentAddToCartButton =
      currentAddToCartButtonContainer
        ?.refs.addToCartButton;

    if (
      !currentAddToCartButtonContainer ||
      (
        !currentAddToCartButton &&
        !acceleratedCheckoutButtonContainer
      )
    ) {
      return;
    }

    if (
      event.detail.resource == null ||
      event.detail.resource.available ==
        false
    ) {
      currentAddToCartButtonContainer
        .disable();

    } else {
      currentAddToCartButtonContainer
        .enable();
    }

    const newAddToCartButton =
      event.detail.data.html
        .querySelector(
          'product-form-component [ref="addToCartButton"]'
        );

    if (
      newAddToCartButton &&
      currentAddToCartButton
    ) {
      morph(
        currentAddToCartButton,
        newAddToCartButton
      );
    }

    if (
      acceleratedCheckoutButtonContainer
    ) {
      if (
        event.detail.resource == null ||
        event.detail.resource.available ==
          false
      ) {
        acceleratedCheckoutButtonContainer
          .setAttribute(
            'hidden',
            'true'
          );

      } else {
        acceleratedCheckoutButtonContainer
          .removeAttribute(
            'hidden'
          );
      }
    }

    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource
          .featured_media
          ?.preview_image
          ?.src;

      if (productVariantMedia) {
        this.refs
          .addToCartButtonContainer
          ?.setAttribute(
            'data-product-variant-media',
            productVariantMedia +
              '&width=100'
          );
      }
    }

    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    const newQuantityInput =
      /** @type {HTMLInputElement | null} */ (
        event.detail.data.html
          .querySelector(
            'quantity-selector-component input[ref="quantityInput"]'
          )
      );

    if (
      quantitySelector
        ?.updateConstraints &&
      newQuantityInput
    ) {
      quantitySelector
        .updateConstraints(
          newQuantityInput.min,
          newQuantityInput.max || null,
          newQuantityInput.step
        );
    }

    const newQuantityRules =
      event.detail.data.html
        .querySelector(
          '.quantity-rules'
        );

    const isQuantityRulesChanging =
      !!quantityRules !==
      !!newQuantityRules;

    const newPricePerItem =
      event.detail.data.html
        .querySelector(
          'price-per-item'
        );

    const isPricePerItemChanging =
      !!pricePerItem !==
      !!newPricePerItem;

    if (
      (
        isQuantityRulesChanging ||
        isPricePerItemChanging
      ) &&
      quantitySelector
    ) {
      const currentQuantityValue =
        quantitySelector.getValue?.();

      const newProductFormButtons =
        event.detail.data.html
          .querySelector(
            '.product-form-buttons'
          );

      if (
        productFormButtons &&
        newProductFormButtons
      ) {
        morph(
          productFormButtons,
          newProductFormButtons
        );

        const newQuantityInputElement =
          /** @type {HTMLInputElement | null} */ (
            event.detail.data.html
              .querySelector(
                'quantity-selector-component input[ref="quantityInput"]'
              )
          );

        if (
          this.refs.quantitySelector
            ?.updateConstraints &&
          newQuantityInputElement &&
          currentQuantityValue
        ) {
          this.refs.quantitySelector
            .setValue(
              currentQuantityValue
            );

          this.refs.quantitySelector
            .updateConstraints(
              newQuantityInputElement.min,
              newQuantityInputElement.max || null,
              newQuantityInputElement.step
            );
        }
      }

    } else {
      const morphTargets = [
        [
          '.quantity-label',
          quantityLabel,
          quantitySelector,
        ],

        [
          '.quantity-rules',
          quantityRules,
          this.refs.productFormButtons,
        ],

        [
          'price-per-item',
          pricePerItem,
          quantitySelectorWrapper,
        ],
      ];

      for (
        const [
          selector,
          currentElement,
          fallback,
        ] of morphTargets
      ) {
        this.#morphOrUpdateElement(
          currentElement,
          event.detail.data.html
            .querySelector(selector),
          fallback
        );
      }
    }

    const currentVolumePricing =
      this.refs.volumePricing;

    const newVolumePricing =
      event.detail.data.html
        .querySelector(
          'volume-pricing'
        );

    this.#morphOrUpdateElement(
      currentVolumePricing,
      newVolumePricing,
      this.refs.productFormButtons
    );

    const hasB2BFeatures =
      quantityRules ||
      newQuantityRules ||
      pricePerItem ||
      newPricePerItem ||
      currentVolumePricing ||
      newVolumePricing;

    if (!hasB2BFeatures) return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   */
  #onVariantSelected = () => {
    this.refs
      .addToCartButtonContainer
      ?.disable();
  };
}

if (
  !customElements.get(
    'product-form-component'
  )
) {
  customElements.define(
    'product-form-component',
    ProductFormComponent
  );
}